import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Table, Tag, Button, message, Tooltip, Alert } from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import { GlobalOutlined } from '@ant-design/icons';
import { buyerService } from '../../services/api';
import { urlMicrositio } from '../../utils/urlMicrositio';
import dayjs from 'dayjs';

const Remates = () => {
  const [loading, setLoading] = useState(true);
  const [remates, setRemates] = useState([]);
  const [solicitando, setSolicitando] = useState({});
  const [searchParams] = useSearchParams();

  // Remate del que venía la persona. Sólo se RESALTA: la solicitud la manda
  // ella con el botón. Un slug viejo autocompletado por el navegador no debe
  // generar pedidos de acceso a cabañas que nadie pidió.
  const eventoSlug = searchParams.get('evento');
  const destacado = eventoSlug
    ? remates.find(r => r.slug === eventoSlug)
    : null;

  useEffect(() => {
    fetchRemates();
  }, []);

  const fetchRemates = async () => {
    setLoading(true);
    try {
      const res = await buyerService.getRemates();
      setRemates(res.data || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar remates');
    } finally {
      setLoading(false);
    }
  };

  const solicitarAcceso = async (id_clientes) => {
    setSolicitando(prev => ({ ...prev, [id_clientes]: true }));
    try {
      await buyerService.solicitarAutorizacion(id_clientes);
      message.success('Solicitud enviada. La cabaña la revisará pronto.');
      await fetchRemates();
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al enviar solicitud');
    } finally {
      setSolicitando(prev => ({ ...prev, [id_clientes]: false }));
    }
  };

  const columnas = [
    {
      title: 'Cabaña',
      dataIndex: ['cabaña', 'razon_social'],
      key: 'cabana',
      render: (val, r) => r.cabaña?.razon_social || `Cabaña #${r.id_clientes}`,
    },
    {
      title: 'Nombre del evento',
      dataIndex: 'nombre_evento',
      key: 'nombre_evento',
    },
    {
      title: 'Fecha inicio',
      dataIndex: 'fecha_inicio',
      key: 'fecha_inicio',
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Estado',
      dataIndex: 'estado_evento',
      key: 'estado',
      render: (v) => {
        if (v === 1) return <Tag color="green">Activo</Tag>;
        if (v === 0) return <Tag color="red">Finalizado</Tag>;
        return <Tag>{v}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      width: 80,
      render: (_, r) =>
        r.slug ? (
          <Tooltip title="Ver micrositio">
            <Button
              type="text"
              icon={<GlobalOutlined />}
              // Respeta el micrositio incrustado: si el productor lo publica en
              // su propio dominio, el comprador tiene que ir ahí y no a la ruta
              // de PUJA, que lo saca del sitio que reconoce.
              href={urlMicrositio({ config: r, eventoSlug: r.slug })}
              target="_blank"
              rel="noopener noreferrer"
            />
          </Tooltip>
        ) : null,
    },
    {
      title: 'Acceso',
      key: 'acceso',
      render: (_, r) => {
        if (r.autorizado) {
          return <Tag color="green">Autorizado</Tag>;
        }
        if (r.solicitud_estado === 'pendiente') {
          return (
            <Button size="small" disabled>
              Solicitud enviada
            </Button>
          );
        }
        return (
          <Button
            size="small"
            type="primary"
            loading={!!solicitando[r.id_clientes]}
            onClick={() => solicitarAcceso(r.id_clientes)}
          >
            Solicitar acceso
          </Button>
        );
      },
    },
  ];

  if (loading) {
    return <PujaSpinner />;
  }

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Remates</h2>

      {destacado && !destacado.autorizado && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Venías del remate "${destacado.nombre_evento}"`}
          description={
            destacado.solicitud_estado === 'pendiente'
              ? 'Ya pediste acceso a esa cabaña. Falta que lo aprueben.'
              // Sin "acá abajo": con pageSize 20 la fila resaltada puede estar
              // en otra página de la tabla y la referencia posicional miente.
              : `Para pre-ofertar necesitás que ${destacado.cabaña?.razon_social || 'la cabaña'} te autorice. Buscá el remate en la lista y tocá "Solicitar acceso".`
          }
        />
      )}

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id_evento"
        dataSource={remates}
        columns={columnas}
        pagination={{ pageSize: 20 }}
        rowClassName={(r) => (destacado && r.id_evento === destacado.id_evento
          ? 'remate-destacado' : '')}
      />
    </div>
  );
};

export default Remates;
